/**
 * Error Handler Utilities for QSAI Frontend
 *
 * This module provides comprehensive error handling utilities for parsing,
 * categorizing, and formatting error responses from the backend API.
 *
 * @module errorHandler
 */

import { FILE_SIZE_LIMITS } from '../constants';

/**
 * Error types supported by the application
 */
export const ERROR_TYPES = {
  VALIDATION_ERROR: 'validation_error',
  BUSINESS_ERROR: 'business_error',
  SYSTEM_ERROR: 'system_error',
  PERMISSION_ERROR: 'permission_error',
  NETWORK_ERROR: 'network_error',
  TIMEOUT_ERROR: 'timeout_error',
};

/**
 * Error severity levels
 */
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
};

/**
 * Default error messages for different error types
 */
const DEFAULT_ERROR_MESSAGES = {
  [ERROR_TYPES.VALIDATION_ERROR]: {
    user_message: 'Please check your input and try again.',
    suggestions: [
      'Review the form fields for any errors',
      'Ensure all required fields are filled',
    ],
  },
  [ERROR_TYPES.BUSINESS_ERROR]: {
    user_message: 'This operation cannot be completed due to business rules.',
    suggestions: [
      'Contact support for assistance',
      'Check if you have the necessary permissions',
    ],
  },
  [ERROR_TYPES.SYSTEM_ERROR]: {
    user_message: 'A system error occurred. Please try again later.',
    suggestions: [
      'Try refreshing the page',
      'Contact support if the problem persists',
    ],
  },
  [ERROR_TYPES.PERMISSION_ERROR]: {
    user_message: 'You do not have permission to perform this action.',
    suggestions: [
      'Contact your administrator',
      'Check if you are logged in with the correct account',
    ],
  },
  [ERROR_TYPES.NETWORK_ERROR]: {
    user_message:
      'Network connection error. Please check your internet connection.',
    suggestions: [
      'Check your internet connection',
      'Try again in a few moments',
    ],
  },
  [ERROR_TYPES.TIMEOUT_ERROR]: {
    user_message: 'The request timed out. Please try again.',
    suggestions: [
      'Try again in a few moments',
      'Contact support if the problem persists',
    ],
  },
};

/**
 * Parse a structured error response from the backend
 *
 * @param {Object|string|Error} errorResponse - The error response to parse
 * @returns {Object} Parsed error object with standardized structure
 *
 * @example
 * // Backend structured error
 * const backendError = {
 *   error: {
 *     code: "PDF_PAGE_LIMIT_EXCEEDED",
 *     type: "validation_error",
 *     message: "Technical message",
 *     user_message: "User-friendly message",
 *     suggestions: ["suggestion1", "suggestion2"],
 *     details: {...}
 *   }
 * };
 *
 * const parsed = parseError(backendError);
 */
export const parseError = (errorResponse) => {
  // Handle null/undefined
  if (!errorResponse) {
    return createFallbackError('Unknown error occurred');
  }

  // Handle string errors
  if (typeof errorResponse === 'string') {
    return createFallbackError(errorResponse);
  }

  // Handle Error objects (including AxiosError)
  if (errorResponse instanceof Error) {
    // For AxiosError, check if it has response data with structured error
    if (errorResponse.response?.data?.error) {
      return normalizeError(errorResponse.response.data.error);
    }

    // Check for new backend error format with errors.system in AxiosError
    if (errorResponse.response?.data?.errors?.system) {
      return normalizeError(errorResponse.response.data.errors.system);
    }

    return createFallbackError(errorResponse.message, {
      name: errorResponse.name,
      stack: errorResponse.stack,
    });
  }

  // Handle axios error responses - try multiple paths
  if (errorResponse.response?.data?.error) {
    return normalizeError(errorResponse.response.data.error);
  }

  // Handle new backend error format with errors.system (check this FIRST before top-level properties)
  if (errorResponse.response?.data?.errors?.system) {
    return normalizeError(errorResponse.response.data.errors.system);
  }

  // Handle direct backend errors.system format (check this BEFORE top-level properties)
  if (errorResponse.errors?.system) {
    return normalizeError(errorResponse.errors.system);
  }

  // Handle direct backend error format
  if (errorResponse.error) {
    return normalizeError(errorResponse.error);
  }

  // Handle already normalized error (check this AFTER nested errors)
  if (errorResponse.code || errorResponse.type) {
    return normalizeError(errorResponse);
  }

  // Handle HTTP error responses
  if (errorResponse.status && errorResponse.statusText) {
    return createHttpError(errorResponse.status, errorResponse.statusText);
  }

  // Fallback for unknown error formats
  return createFallbackError('An unexpected error occurred', errorResponse);
};

/**
 * Normalize an error object to ensure it has all required fields
 *
 * @param {Object} error - Error object to normalize
 * @returns {Object} Normalized error object
 */
const normalizeError = (error) => {
  const errorType = error.type || ERROR_TYPES.SYSTEM_ERROR;
  const defaults =
    DEFAULT_ERROR_MESSAGES[errorType] ||
    DEFAULT_ERROR_MESSAGES[ERROR_TYPES.SYSTEM_ERROR];

  return {
    code: error.code || 'UNKNOWN_ERROR',
    type: errorType,
    message: error.message || error.user_message || defaults.user_message,
    user_message: error.user_message || defaults.user_message,
    suggestions: Array.isArray(error.suggestions)
      ? error.suggestions
      : defaults.suggestions,
    details: error.details || {},
    reference_id: error.reference_id || generateReferenceId(),
    severity: error.severity || getSeverityFromType(errorType),
    timestamp: error.timestamp || new Date().toISOString(),
  };
};

/**
 * Create a fallback error for unknown error formats
 *
 * @param {string} message - Error message
 * @param {Object} details - Additional error details
 * @returns {Object} Fallback error object
 */
const createFallbackError = (message, details = {}) => {
  return normalizeError({
    code: 'UNKNOWN_ERROR',
    type: ERROR_TYPES.SYSTEM_ERROR,
    message: message || 'An unexpected error occurred',
    user_message: message || 'An unexpected error occurred',
    details,
  });
};

/**
 * Create an HTTP error from status code and message
 *
 * @param {number} status - HTTP status code
 * @param {string} statusText - HTTP status text
 * @returns {Object} HTTP error object
 */
const createHttpError = (status, statusText) => {
  let errorType = ERROR_TYPES.SYSTEM_ERROR;
  let code = `HTTP_${status}`;
  let userMessage = 'An error occurred while processing your request.';

  // Map HTTP status codes to error types and messages
  if (status >= 400 && status < 500) {
    errorType =
      status === 403
        ? ERROR_TYPES.PERMISSION_ERROR
        : ERROR_TYPES.VALIDATION_ERROR;

    switch (status) {
      case 400:
        userMessage = 'Invalid request. Please check your input and try again.';
        break;
      case 401:
        userMessage = 'Authentication required. Please log in and try again.';
        break;
      case 403:
        userMessage = 'You do not have permission to perform this action.';
        break;
      case 404:
        userMessage = 'The requested resource was not found.';
        break;
      case 408:
        userMessage = 'Request timeout. Please try again.';
        errorType = ERROR_TYPES.TIMEOUT_ERROR;
        break;
      case 429:
        userMessage = 'Too many requests. Please wait a moment and try again.';
        break;
      case 413:
        userMessage = `! Total file size exceeded limit
                      The combined size of all files exceeded the maximum allowed size of ${FILE_SIZE_LIMITS.MAX_TOTAL / (1024 * 1024)}MB.
                      Please remove some files or upload smaller files.`;
        break;
      default:
        userMessage = 'Invalid request. Please check your input and try again.';
    }
  } else if (status >= 500) {
    errorType = ERROR_TYPES.SYSTEM_ERROR;
    userMessage = 'Server error. Please try again later.';
  }

  return normalizeError({
    code,
    type: errorType,
    message: `${status} ${statusText}`,
    user_message: userMessage,
    details: { status, statusText },
  });
};

/**
 * Get user-friendly error message from error object
 *
 * @param {Object} error - Error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  const parsedError = parseError(error);
  return (
    parsedError.user_message ||
    parsedError.message ||
    'An unexpected error occurred'
  );
};

/**
 * Get error code from error object
 *
 * @param {Object} error - Error object
 * @returns {string} Error code
 */
export const getErrorCode = (error) => {
  const parsedError = parseError(error);
  return parsedError.code || 'UNKNOWN_ERROR';
};

/**
 * Get error type from error object
 *
 * @param {Object} error - Error object
 * @returns {string} Error type
 */
export const getErrorType = (error) => {
  const parsedError = parseError(error);
  return parsedError.type || ERROR_TYPES.SYSTEM_ERROR;
};

/**
 * Format suggestions as actionable items
 *
 * @param {Array<string>} suggestions - Array of suggestion strings
 * @returns {Array<Object>} Array of formatted suggestion objects
 */
export const formatSuggestions = (suggestions) => {
  if (!Array.isArray(suggestions)) {
    return [];
  }

  return suggestions.map((suggestion, index) => ({
    id: index,
    text: suggestion,
    actionable: isActionableSuggestion(suggestion),
  }));
};

/**
 * Check if an error is retryable
 *
 * @param {Object} error - Error object
 * @returns {boolean} True if error is retryable
 */
export const isRetryableError = (error) => {
  const parsedError = parseError(error);
  const retryableTypes = [
    ERROR_TYPES.NETWORK_ERROR,
    ERROR_TYPES.TIMEOUT_ERROR,
    ERROR_TYPES.SYSTEM_ERROR,
  ];

  return retryableTypes.includes(parsedError.type);
};

/**
 * Check if an error is a validation error
 *
 * @param {Object} error - Error object
 * @returns {boolean} True if error is validation error
 */
export const isValidationError = (error) => {
  const parsedError = parseError(error);
  return parsedError.type === ERROR_TYPES.VALIDATION_ERROR;
};

/**
 * Check if an error is a permission error
 *
 * @param {Object} error - Error object
 * @returns {boolean} True if error is permission error
 */
export const isPermissionError = (error) => {
  const parsedError = parseError(error);
  return parsedError.type === ERROR_TYPES.PERMISSION_ERROR;
};

/**
 * Check if an error is a business error
 *
 * @param {Object} error - Error object
 * @returns {boolean} True if error is business error
 */
export const isBusinessError = (error) => {
  const parsedError = parseError(error);
  return parsedError.type === ERROR_TYPES.BUSINESS_ERROR;
};

/**
 * Get severity level from error type
 *
 * @param {string} errorType - Error type
 * @returns {string} Severity level
 */
const getSeverityFromType = (errorType) => {
  switch (errorType) {
    case ERROR_TYPES.VALIDATION_ERROR:
      return ERROR_SEVERITY.MEDIUM;
    case ERROR_TYPES.BUSINESS_ERROR:
      return ERROR_SEVERITY.MEDIUM;
    case ERROR_TYPES.PERMISSION_ERROR:
      return ERROR_SEVERITY.HIGH;
    case ERROR_TYPES.SYSTEM_ERROR:
      return ERROR_SEVERITY.HIGH;
    case ERROR_TYPES.NETWORK_ERROR:
      return ERROR_SEVERITY.MEDIUM;
    case ERROR_TYPES.TIMEOUT_ERROR:
      return ERROR_SEVERITY.LOW;
    default:
      return ERROR_SEVERITY.MEDIUM;
  }
};

/**
 * Check if a suggestion is actionable (contains action words)
 *
 * @param {string} suggestion - Suggestion text
 * @returns {boolean} True if suggestion is actionable
 */
const isActionableSuggestion = (suggestion) => {
  const actionWords = [
    'try',
    'check',
    'verify',
    'contact',
    'ensure',
    'review',
    'update',
  ];
  return actionWords.some((word) => suggestion.toLowerCase().includes(word));
};

/**
 * Generate a unique reference ID for error tracking
 *
 * @returns {string} Unique reference ID
 */
const generateReferenceId = () => {
  return `err_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Create error summary for reporting
 *
 * @param {Object} error - Error object
 * @returns {Object} Error summary object
 */
export const createErrorSummary = (error) => {
  const parsedError = parseError(error);

  return {
    code: parsedError.code,
    type: parsedError.type,
    message: parsedError.user_message,
    severity: parsedError.severity,
    reference_id: parsedError.reference_id,
    timestamp: parsedError.timestamp,
    retryable: isRetryableError(parsedError),
  };
};

/**
 * Error handler middleware for API requests
 *
 * @param {Object} error - Error object from API request
 * @param {Object} options - Handler options
 * @returns {Object} Processed error object
 */
export const handleApiError = (error, options = {}) => {
  const {
    defaultMessage = 'An error occurred while processing your request.',
  } = options;

  const parsedError = parseError(error);

  // Override default message if provided
  if (defaultMessage && !parsedError.user_message) {
    parsedError.user_message = defaultMessage;
  }

  return parsedError;
};

/**
 * Utility function to check if error contains specific code
 *
 * @param {Object} error - Error object
 * @param {string} code - Error code to check
 * @returns {boolean} True if error contains the specified code
 */
export const hasErrorCode = (error, code) => {
  const parsedError = parseError(error);
  return parsedError.code === code;
};

/**
 * Utility function to check if error contains specific type
 *
 * @param {Object} error - Error object
 * @param {string} type - Error type to check
 * @returns {boolean} True if error contains the specified type
 */
export const hasErrorType = (error, type) => {
  const parsedError = parseError(error);
  return parsedError.type === type;
};

/**
 * Default export object with all utility functions
 */
export default {
  parseError,
  getErrorMessage,
  getErrorCode,
  getErrorType,
  formatSuggestions,
  isRetryableError,
  isValidationError,
  isPermissionError,
  isBusinessError,
  createErrorSummary,
  handleApiError,
  hasErrorCode,
  hasErrorType,
  ERROR_TYPES,
  ERROR_SEVERITY,
};
